import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { createCustomStateSetOnRegexHandler, guidRegex } from '@appState/helpers/state-reset-handler';
import { getAllWithParams, StateDictionary } from '@appState/helpers/store-base';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { NewsArticleErrorLog, NewsArticleErrorLogQueryParams } from 'src/models';
import { NewsArticleLogsService } from './news-article-logs.service';

interface NewsArticleLogsState {
  logs: NewsArticleErrorLog[];
  isLoading: boolean;
  selectedLogId: string | null;
}

const initialState: NewsArticleLogsState = {
  logs: [],
  isLoading: false,
  selectedLogId: null,
};

const logsDictionary: StateDictionary<NewsArticleErrorLog> = {
  entityList: 'logs',
  listLoading: 'isLoading',
  selectedEntityLoading: '',
  selectedEntity: '',
  entityId: 'id',
  createLoading: '',
  sortFn: (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
};

export const NewsArticleLogsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('news-article-logs-store'),
  withComputed(state => ({
    selectedLog: computed(() => state.logs().find(log => log.id === state.selectedLogId()) ?? null),
  })),
  withMethods((store, logsService = inject(NewsArticleLogsService), router = inject(Router)) => {
    createCustomStateSetOnRegexHandler<NewsArticleLogsState>(new RegExp(`^/marketing/communities/${guidRegex}$`))(store, {
      logs: [],
      selectedLogId: null,
    });
    createCustomStateSetOnRegexHandler<NewsArticleLogsState>(new RegExp(`^/marketing/communities/${guidRegex}/news-logs$`))(store, {
      selectedLogId: null,
    });

    const repositoryMethods = {
      getAll: getAllWithParams<NewsArticleErrorLog, NewsArticleErrorLogQueryParams>({
        store,
        service: logsService,
        dictionary: logsDictionary,
      }),

      startLoading: (): void => {
        patchState(store, {
          isLoading: true,
        });
      },
    };

    const navigationActions = {
      selectLog: (id: string): void => {
        patchState(store, {
          selectedLogId: id,
        });
      },
      goToActions: async (id: string, communityId: string): Promise<boolean> => {
        navigationActions.selectLog(id);
        const result = await router.navigate(['/marketing', 'communities', communityId, 'news-logs', id]);
        if (!result) return false;

        return result;
      },
    };

    return {
      ...repositoryMethods,
      ...navigationActions,
    };
  }),
);

export interface NewsArticleLogsStoreType {
  readonly logs: Signal<NewsArticleErrorLog[]>;
  readonly isLoading: Signal<boolean>;
  readonly selectedLogId: Signal<string | null>;

  readonly selectedLog: Signal<NewsArticleErrorLog | null>;

  readonly getAll: (params: NewsArticleErrorLogQueryParams) => void;
  readonly startLoading: () => void;

  readonly selectLog: (id: string) => void;
  readonly goToActions: (id: string, communityId: string) => Promise<boolean>;
}
